import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  forkJoin,
  observable,
  Observable,
  Subscription,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { CraftingService } from '../modules/main-game/components/game-modals/services/crafting.service';
import { NearApiServiceService } from '../modules/main-game/services/near-api-service.service';
import { logout } from '../store/auth/auth.actions';
import {
  createCompletedCharacterSlots,
  setCharacterState,
  updateCharacterSlot,
} from '../store/characterState/characterState.actions';
import { setInitialConfigurationsData } from '../store/configurationState/configurationState.actions';
import {
  setInitialMapData,
  updateMapData,
} from '../store/globalMapState/globalMapState.actions';
import {
  setAccountState,
  setCardState,
  setRefferalState,
  setStateCondition,
} from '../store/mainState/mainState.actions';
import {
  accountEndpoints,
  adminEndpoints,
  authEndpoints,
  buildingCraftEndpoints,
  buildingEnergyCraftEndpoints,
  cardEndpoints,
  characterEndpoints,
  checkApiHealthStatusEndpoint,
  cityEndpoints,
  configurationEndpoints,
  dailyQuestEndpoints,
  globalMapEndpoints,
  referralEndpoints,
} from './api.endpoints';
import { ApiWebSocketService } from './api.web-socket.service';
import {
  IApiAdminRequestBalanceBody,
  IApiAdminRequestCardBody,
} from './interfaces/admin.interface';
import {
  IApiAccelerateRequestBody,
  IApiCraftCollect,
  IApiCraftCollectRequestBody,
  IApiStartCraftRequestBody,
  IApiStartEnergyCraftRequestBody,
  IApiStartEnergyCraftResponseBody,
} from './interfaces/buildingCraft.interface';
import { INewCardState } from './interfaces/card.interface';
import {
  IApiCharacterInfo,
  IApiCharacterListResponse,
  ICharacterReplenishment,
} from './interfaces/character.interface';
import { IApiCityResponse, IBuildingCraft } from './interfaces/city.interface';
import {
  IApiAccountResponse,
  IApiConfigurationResponse,
} from './interfaces/configuration.interface';
import {
  IMapMatrixRequest,
  IMapMatrixResponse,
  IMapUnlockLandRequest,
} from './interfaces/global-map.interface';
import {
  IPubDailyQuest,
  IPubStartDailyQuest,
} from './interfaces/pub.interface';
import { IApiReferralState } from './interfaces/referral.interface';

interface IApiRefreshResponse {
  accessToken: string;
  expirationAccessToken: string;
  expirationRefreshToken: string;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  public mapConfig: IMapMatrixRequest = {
    coordinateX: 0,
    coordinateY: 0,
    // for testng //radius: 6,
    radius: 2,
  };
  public fullScreenMode: boolean = false;
  public isStateFetched: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private configs: IApiConfigurationResponse | undefined;
  constructor(
    private http: HttpClient,
    private store: Store,
    private ws: ApiWebSocketService,
    private nearApiService: NearApiServiceService,
    private craftingService: CraftingService
  ) {}

  getMainState(): void {
    this.checkApiHealthStatus().subscribe(
      (res) => {
        this.ws.connectWebSockets();
        forkJoin([
          this.accountState(),
          this.characterState(),
          this.configState(),
          this.cardState(),
          this.getGlobalMapMatrix(this.mapConfig),
          this.refferalState(),
        ]).subscribe((res) => {
          this.store.dispatch(setAccountState({ responseData: res[0] }));
          this.store.dispatch(setCharacterState({ responseData: res[1] }));
          this.store.dispatch(
            createCompletedCharacterSlots({ configs: res[2] })
          );
          this.store.dispatch(
            setInitialConfigurationsData({ responseData: res[2] })
          );
          this.store.dispatch(setCardState({ responseData: res[3] }));
          this.store.dispatch(setInitialMapData({ responseData: res[4] }));
          this.store.dispatch(setRefferalState({ responseData: res[5] }));
          this.store.dispatch(setStateCondition({ ready: true }));
          this.configs = res[2];
          /// init wallet ///
          this.nearApiService.initWallet(
            'Fishmaster',
            res[2].blockchainConfigurations.find((el) => el.type === 'NEAR'),
            res[0]
          );
          this.isStateFetched.next(true);
        });
      },
      (err) => {
        this.store.dispatch(logout());
      }
    );
  }
  checkApiHealthStatus(): Observable<Array<number>> {
    return this.http.get<Array<number>>(
      environment.apiUrl + checkApiHealthStatusEndpoint
    );
  }
  auth(
    type: 'refresh' | 'logout',
    params?: string
  ): Observable<IApiRefreshResponse | {} | boolean> {
    switch (type) {
      case 'refresh':
        return this.http.post(
          environment.apiUrl +
            authEndpoints.post.refreshToken +
            localStorage['refreshToken'],
          {}
        );
      case 'logout':
        return this.http.post(
          environment.apiUrl + authEndpoints.post.logout,
          {}
        );
      default:
        return new BehaviorSubject<boolean>(false);
    }
  }
  getGlobalMapMatrix(
    matrixConfig: IMapMatrixRequest
  ): Observable<IMapMatrixResponse[]> {
    return this.http.post<IMapMatrixResponse[]>(
      environment.apiUrl + globalMapEndpoints.post.matrix,
      matrixConfig
    );
  }
  // getUpdatedMap(matrixConfig: IMapMatrixRequest): void {
  //   this.http
  //     .post<IMapMatrixResponse[]>(
  //       environment.apiUrl + globalMapEndpoints.post.matrix,
  //       matrixConfig
  //     )
  //     .subscribe((res) => {
  //       this.store.dispatch(updateMapData({ responseData: res }));
  //     });
  // }
  unlockMapLand(
    unlockLandXY: IMapUnlockLandRequest
  ): Observable<IMapMatrixResponse> {
    return this.http.post<IMapMatrixResponse>(
      environment.apiUrl + globalMapEndpoints.post.unlock,
      unlockLandXY
    );
  }
  accountState(): Observable<IApiAccountResponse> {
    return this.http.get<IApiAccountResponse>(
      environment.apiUrl + accountEndpoints.get.accountInfo
    );
  }

  characterState(): Observable<IApiCharacterListResponse> {
    return this.http.get<IApiCharacterListResponse>(
      environment.apiUrl + characterEndpoints.get.characterList
    );
  }

  configState(): Observable<IApiConfigurationResponse> {
    return this.http.get<IApiConfigurationResponse>(
      environment.apiUrl + configurationEndpoints.get.configuration
    );
  }

  cardState(): Observable<INewCardState> {
    return this.http.get<INewCardState>(
      environment.apiUrl + cardEndpoints.get.cardState
    );
  }

  cityState(cityId: number): Observable<IApiCityResponse> {
    return this.http.get<IApiCityResponse>(
      `${environment.apiUrl + cityEndpoints.get.city}${cityId}`
    );
  }
  refferalState(): Observable<IApiReferralState> {
    return this.http.get<IApiReferralState>(
      environment.apiUrl + referralEndpoints.get.referral
    );
  }
  dailyQuestActions(
    action: 'update' | 'start' | 'collect' | 'cancel',
    buildingDailyTaskId?: number | string,
    requestBody?: IPubStartDailyQuest,
    buildingId?: number
  ): Observable<IPubDailyQuest> {
    if (action === 'update') {
      return this.http.get<IPubDailyQuest>(
        environment.apiUrl + 'building/1/' + dailyQuestEndpoints.get
      );
    }
    if (action === 'start' && requestBody) {
      return this.http.post<IPubDailyQuest>(
        environment.apiUrl +
          dailyQuestEndpoints.post.dailyQuestAction +
          'start',
        requestBody
      );
    }
    if (buildingDailyTaskId && (action === 'cancel' || action === 'collect')) {
      return this.http.post<IPubDailyQuest>(
        environment.apiUrl +
          dailyQuestEndpoints.post.dailyQuestAction +
          `${buildingDailyTaskId}/${action}`,
        null
      );
    }
    return new Observable(undefined);
  }
  characterSlotActions(
    type: 'slot' | 'skill',
    action: 'unlock' | 'fill' | 'free-up' | 'redetermine',
    slotNumber: number
    // name?: string
  ): void {
    let req: Observable<IApiCharacterInfo> | undefined;
    req = this.http.post<IApiCharacterInfo>(
      `${environment.apiUrl}${characterEndpoints.post[type]}${slotNumber}/${action}`,
      {}
    );

    req.subscribe((res) => {
      this.store.dispatch(updateCharacterSlot({ slot: res }));
      if (this.configs)
        this.store.dispatch(
          createCompletedCharacterSlots({ configs: this.configs })
        );
    });
  }
  characterReplenishmentActions(
    type: 'energyReplenishment' | 'expirenceReplenishment',
    body: ICharacterReplenishment
  ): Observable<boolean> {
    return this.http.post<boolean>(
      environment.apiUrl + characterEndpoints.post[type],
      body
    );
  }
  buildingCraftActions(
    action: 'startCraft' | 'collect' | 'accelerate',
    requestBody:
      | IApiStartCraftRequestBody
      | IApiCraftCollectRequestBody
      | IApiAccelerateRequestBody
  ): Observable<IBuildingCraft | IApiCraftCollect | IBuildingCraft | any> {
    return this.http.post(
      environment.apiUrl + buildingCraftEndpoints.post[action],
      requestBody
    );
  }
  buildingEnergyCraftActions(
    requestBody: IApiStartEnergyCraftRequestBody
  ): Observable<IApiStartEnergyCraftResponseBody> {
    return this.http.post<IApiStartEnergyCraftResponseBody>(
      environment.apiUrl + buildingEnergyCraftEndpoints.post.startCraft,
      requestBody
    );
  }
  adminActions(action: {
    type: 'card' | 'accountReset' | 'balance' | 'energyReset';
    body: IApiAdminRequestBalanceBody | IApiAdminRequestCardBody | null;
  }): Observable<object> {
    return this.http.post(
      environment.apiUrl + adminEndpoints.post[action.type],
      action.body
    );
  }
  unsubscribe(subscruptions: Subscription[]): void {
    subscruptions.forEach((subsc) => subsc.unsubscribe());
  }
}
